import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Truck, ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image with Overlay */}
      <div 
        className="hidden lg:flex lg:w-[60%] relative bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1759272548470-d0686d071036?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxsb2dpc3RpY3MlMjB0cnVjayUyMHNoaXBwaW5nJTIwY29udGFpbmVyfGVufDB8fHx8MTc3MTY5ODkzMnww&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary-blue/90 to-primary-blue/50"></div>
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-accent-blue rounded-lg flex items-center justify-center">
              <Truck className="w-7 h-7" />
            </div>
            <span className="text-3xl font-bold font-manrope">LogisticsHub</span>
          </div>
          <h1 className="text-5xl font-bold font-manrope mb-6 leading-tight">
            Password Recovery
          </h1>
          <p className="text-xl text-white/90 font-ibm leading-relaxed">
            Don't worry! We'll help you reset your password<br />and get back to managing your operations.
          </p>
        </div>
      </div>

      {/* Right Side - Forgot Password Form */}
      <div className="flex-1 flex items-center justify-center bg-white p-8">
        <div className="w-full max-w-md">
          {!submitted ? (
            <>
              {/* Back to Login Link */}
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 text-accent-blue hover:text-accent-blue/80 font-ibm font-medium mb-8 transition-colors"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>

              <div className="mb-8">
                <h2 className="text-3xl font-bold text-text-dark font-manrope mb-2">Forgot Password?</h2>
                <p className="text-text-secondary font-ibm">
                  Enter your email address and we'll send you instructions to reset your password.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="reset-email" className="text-text-dark font-ibm font-medium">
                    Email Address
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="reset-email"
                      type="email"
                      data-testid="reset-email-input"
                      placeholder="your.email@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12 pl-11 border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                      required
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  data-testid="send-reset-link-button"
                  className="w-full h-12 bg-accent-blue hover:bg-accent-blue/90 text-white text-base font-semibold font-ibm rounded-lg transition-colors"
                >
                  Send Reset Link
                </Button>
              </form>

              {/* Footer */}
              <div className="mt-8 text-center">
                <p className="text-sm text-text-secondary font-ibm">
                  Remember your password?{' '}
                  <Link to="/" className="text-accent-blue hover:text-accent-blue/80 font-medium">
                    Sign In
                  </Link>
                </p>
              </div>
            </>
          ) : (
            /* Success Message */
            <div className="text-center">
              <div className="w-16 h-16 bg-accent-green/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-accent-green" />
              </div>
              
              <h2 className="text-3xl font-bold text-text-dark font-manrope mb-4">Check Your Email</h2>
              <p className="text-text-secondary font-ibm mb-8 leading-relaxed">
                We've sent password reset instructions to<br />
                <span className="font-semibold text-text-dark">{email}</span>
              </p>
              
              <div className="space-y-4">
                <p className="text-sm text-text-secondary font-ibm">
                  Didn't receive the email? Check your spam folder or
                </p>
                <Button
                  onClick={() => setSubmitted(false)}
                  variant="outline"
                  data-testid="resend-link-button"
                  className="w-full h-12 border-accent-blue text-accent-blue hover:bg-accent-blue/5 text-base font-semibold font-ibm rounded-lg transition-colors"
                >
                  Resend Reset Link
                </Button>
              </div>

              <div className="mt-8">
                <Link 
                  to="/" 
                  className="text-accent-blue hover:text-accent-blue/80 font-ibm font-medium transition-colors"
                  data-testid="back-to-login-button"
                >
                  ← Back to Login
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
